import { useState, useCallback, useEffect, useRef } from 'react';
import { getUserById } from './users';
import { getOfferCountdown } from '@/utils/offerCountdown';
import { supabase } from '@/integrations/supabase/client';
import { uploadDataUrl } from '@/services/storage';
import { track, AnalyticsEvent } from '@/lib/analytics';
import { submitReport } from '@/services/reports';


export type PostType = 'player' | 'club' | 'offer' | 'media' | 'coach' | 'recruiter';

export interface CommentMention {
  userId: string;
  username: string;
}

export interface FeedComment {
  id: string;
  postId: string;
  authorId: string;
  authorName: string;
  authorInitials: string;
  text: string;
  createdAt: string;
  likes: string[];
  parentCommentId?: string;
  mentions?: CommentMention[];
  hidden?: boolean;
}

export interface MediaItem {
  url: string;
  type: 'image' | 'video';
  thumbnail?: string;
}

export interface FeedPost {
  id: string;
  authorId: string;
  authorName: string;
  authorInitials: string;
  category?: string;
  postType?: PostType;
  content: string;
  imageUrl?: string;
  mediaItems?: MediaItem[];
  createdAt: string;
  likes: string[];
  comments: FeedComment[];
  savedBy: string[];
  repostedBy: string[];
  originalPostId?: string;
  originalAuthorId?: string;
  originalAuthorName?: string;
  offerId?: string;
  archived: boolean;
  taggedUserIds?: string[];
  pinnedCommentId?: string;
}

export interface PostReport {
  id: string;
  postId: string;
  reporterId: string;
  reason: string;
  createdAt: string;
}

export type CommentReportReason = 'spam' | 'harassment' | 'insult' | 'inappropriate' | 'off-topic' | 'other';

export interface CommentReport {
  id: string;
  commentId: string;
  postId: string;
  reporterId: string;
  reason: CommentReportReason;
  createdAt: string;
}

let nextId = 100;
const genId = () => String(nextId++);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const asUuid = (value?: string) => (value && UUID_RE.test(value) ? value : null);

const authorName = (id: string) => getUserById(id)?.name ?? 'Unknown';
const authorInitials = (id: string) => getUserById(id)?.initials ?? '??';

function sortByRelevance(posts: FeedPost[], userRole?: string, offers?: import('@/store/offers').Offer[]): FeedPost[] {
  const weights: Record<string, Partial<Record<PostType, number>>> = {
    player:    { player: 3, media: 3, offer: 2, club: 1, coach: 2, recruiter: 2 },
    coach:     { player: 2, offer: 3, media: 2, club: 1, coach: 3, recruiter: 2 },
    recruiter: { offer: 3, player: 2, media: 2, club: 1, coach: 2, recruiter: 3 },
    club:      { club: 3, offer: 2, media: 2, player: 1, coach: 2, recruiter: 2 },
    media:     { media: 3, player: 2, offer: 2, club: 1, coach: 1, recruiter: 1 },
    admin:     { player: 1, club: 1, offer: 1, media: 1, coach: 1, recruiter: 1 },
  };

  const roleWeights = weights[userRole ?? 'player'] ?? weights.player;

  const isExpired = (p: FeedPost): boolean => {
    if (p.postType !== 'offer' || !p.offerId || !offers) return false;
    const o = offers.find((x) => x.id === p.offerId);
    return o ? getOfferCountdown(o.date).expired : false;
  };

  return [...posts].sort((a, b) => {
    // Expired offer posts go to the bottom
    const expA = isExpired(a) ? 1 : 0;
    const expB = isExpired(b) ? 1 : 0;
    if (expA !== expB) return expA - expB;

    const wA = roleWeights[a.postType ?? 'player'] ?? 1;
    const wB = roleWeights[b.postType ?? 'player'] ?? 1;
    if (wB !== wA) return wB - wA;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

export const FEED_PAGE_SIZE = 20;

const POST_COLUMNS =
  'id, author_id, content, media_url, media_items, post_type, category, offer_id, tagged_user_ids, original_post_id, original_author_id, archived, created_at';

/**
 * Fetch one page of posts plus the likes, saves and comments that belong to
 * those posts only (never the whole tables).
 */
async function fetchFeed(page = 0): Promise<FeedPost[]> {
  const from = page * FEED_PAGE_SIZE;
  const postsRes = await supabase
    .from('posts')
    .select(POST_COLUMNS)
    .order('created_at', { ascending: false })
    .range(from, from + FEED_PAGE_SIZE - 1);

  if (postsRes.error) throw postsRes.error;
  const rows = postsRes.data ?? [];
  if (rows.length === 0) return [];

  const ids = rows.map((r) => r.id);
  const [likesRes, savesRes, commentsRes, repostsRes] = await Promise.all([
    supabase.from('likes').select('post_id, user_id').in('post_id', ids),
    supabase.from('saved_posts').select('post_id, user_id').in('post_id', ids),
    supabase
      .from('comments')
      .select('id, post_id, author_id, content, created_at, parent_id, pinned')
      .in('post_id', ids)
      .order('created_at', { ascending: true }),
    supabase.from('posts').select('original_post_id, author_id').in('original_post_id', ids),
  ]);

  const firstError = likesRes.error || savesRes.error || commentsRes.error || repostsRes.error;
  if (firstError) throw firstError;


  const likesByPost = new Map<string, string[]>();
  (likesRes.data ?? []).forEach((l) => {
    likesByPost.set(l.post_id, [...(likesByPost.get(l.post_id) ?? []), l.user_id]);
  });
  const savesByPost = new Map<string, string[]>();
  (savesRes.data ?? []).forEach((s) => {
    savesByPost.set(s.post_id, [...(savesByPost.get(s.post_id) ?? []), s.user_id]);
  });
  const commentsByPost = new Map<string, FeedComment[]>();
  const pinnedByPost = new Map<string, string>();
  (commentsRes.data ?? []).forEach((c) => {
    const comment: FeedComment = {
      id: c.id,
      postId: c.post_id,
      authorId: c.author_id,
      authorName: authorName(c.author_id),
      authorInitials: authorInitials(c.author_id),
      text: c.content,
      createdAt: c.created_at,
      likes: [],
      parentCommentId: c.parent_id ?? undefined,
    };
    commentsByPost.set(c.post_id, [...(commentsByPost.get(c.post_id) ?? []), comment]);
    if (c.pinned) pinnedByPost.set(c.post_id, c.id);
  });
  const repostersByPost = new Map<string, string[]>();
  (repostsRes.data ?? []).forEach((r) => {
    if (!r.original_post_id) return;
    repostersByPost.set(r.original_post_id, [...(repostersByPost.get(r.original_post_id) ?? []), r.author_id]);
  });


  return rows.map((r) => ({
    id: r.id,
    authorId: r.author_id,
    authorName: authorName(r.author_id),
    authorInitials: authorInitials(r.author_id),
    category: r.category ?? undefined,
    postType: (r.post_type ?? undefined) as PostType | undefined,
    content: r.content ?? '',
    imageUrl: r.media_url ?? undefined,
    mediaItems: (r.media_items as unknown as MediaItem[] | null) ?? undefined,
    createdAt: r.created_at,
    likes: likesByPost.get(r.id) ?? [],
    comments: commentsByPost.get(r.id) ?? [],
    savedBy: savesByPost.get(r.id) ?? [],
    repostedBy: repostersByPost.get(r.id) ?? [],
    originalPostId: r.original_post_id ?? undefined,
    originalAuthorId: r.original_author_id ?? undefined,
    originalAuthorName: r.original_author_id ? authorName(r.original_author_id) : undefined,
    offerId: r.offer_id ?? undefined,
    archived: !!r.archived,
    taggedUserIds: r.tagged_user_ids?.length ? r.tagged_user_ids : undefined,
    pinnedCommentId: pinnedByPost.get(r.id),
  }));
}

export function useFeedStore(currentUserId: string) {
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<PostReport[]>([]);
  const [commentReports, setCommentReports] = useState<CommentReport[]>([]);
  const [lastCommentTimes, setLastCommentTimes] = useState<{ text: string; time: number }[]>([]);
  const [commentingDisabled, setCommentingDisabled] = useState<Set<string>>(new Set());
  const loadedFor = useRef<string | null>(null);
  const pageRef = useRef(0);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    pageRef.current = 0;
    try {
      const page = await fetchFeed(0);
      setPosts(page);
      setHasMore(page.length === FEED_PAGE_SIZE);
    } catch (err) {
      console.error('[feed] load failed', err);
      setError(err instanceof Error ? err.message : 'Failed to load the feed');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const page = await fetchFeed(next);
      pageRef.current = next;
      setPosts((prev) => {
        const known = new Set(prev.map((p) => p.id));
        return [...prev, ...page.filter((p) => !known.has(p.id))];
      });
      setHasMore(page.length === FEED_PAGE_SIZE);
    } catch (err) {
      console.error('[feed] load more failed', err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loading, loadingMore]);


  useEffect(() => {
    if (!currentUserId || currentUserId === '__none__') {
      setPosts([]);
      setLoading(false);
      return;
    }
    if (loadedFor.current === currentUserId) return;
    loadedFor.current = currentUserId;
    void reload();
  }, [currentUserId, reload]);

  const toggleLike = useCallback((postId: string) => {
    let liked = false;
    void track(AnalyticsEvent.POST_LIKED, { post_id: postId });
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id !== postId) return p;
        liked = p.likes.includes(currentUserId);
        return { ...p, likes: liked ? p.likes.filter((id) => id !== currentUserId) : [...p.likes, currentUserId] };
      })
    );
    void (async () => {
      const { error: err } = liked
        ? await supabase.from('likes').delete().eq('post_id', postId).eq('user_id', currentUserId)
        : await supabase.from('likes').insert({ post_id: postId, user_id: currentUserId });
      if (err) console.warn('[feed] toggleLike failed', err);
    })();
  }, [currentUserId]);

  const addComment = useCallback((postId: string, text: string, parentCommentId?: string, mentions?: CommentMention[]) => {
    if (!text.trim()) return;
    const user = getUserById(currentUserId);
    const tempId = genId();
    const comment: FeedComment = {
      id: tempId, postId, authorId: currentUserId,
      authorName: user?.name ?? 'Unknown', authorInitials: user?.initials ?? '??',
      text: text.trim(), createdAt: new Date().toISOString(),
      likes: [], parentCommentId,
      mentions: mentions?.length ? mentions : undefined,
    };
    setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, comments: [...p.comments, comment] } : p));
    void track(AnalyticsEvent.COMMENT_CREATED, { post_id: postId, is_reply: Boolean(parentCommentId) });
    void (async () => {
      const { data, error: err } = await supabase
        .from('comments')
        .insert({ post_id: postId, author_id: currentUserId, content: text.trim(), parent_id: asUuid(parentCommentId) })
        .select('id')
        .maybeSingle();
      if (err || !data) {
        console.warn('[feed] addComment failed', err);
        return;
      }
      setPosts((prev) => prev.map((p) => p.id === postId
        ? { ...p, comments: p.comments.map((c) => (c.id === tempId ? { ...c, id: data.id } : c)) }
        : p));
    })();
    return comment;
  }, [currentUserId]);

  // Comment likes are a client-side interaction (no dedicated table yet).
  const toggleCommentLike = useCallback((postId: string, commentId: string) => {
    setPosts((prev) => prev.map((p) => {
      if (p.id !== postId) return p;
      return {
        ...p,
        comments: p.comments.map((c) => {
          if (c.id !== commentId) return c;
          const liked = c.likes.includes(currentUserId);
          return { ...c, likes: liked ? c.likes.filter((id) => id !== currentUserId) : [...c.likes, currentUserId] };
        }),
      };
    }));
  }, [currentUserId]);

  const toggleSave = useCallback((postId: string) => {
    let saved = false;
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id !== postId) return p;
        saved = p.savedBy.includes(currentUserId);
        return { ...p, savedBy: saved ? p.savedBy.filter((id) => id !== currentUserId) : [...p.savedBy, currentUserId] };
      })
    );
    void (async () => {
      const { error: err } = saved
        ? await supabase.from('saved_posts').delete().eq('post_id', postId).eq('user_id', currentUserId)
        : await supabase.from('saved_posts').insert({ post_id: postId, user_id: currentUserId });
      if (err) console.warn('[feed] toggleSave failed', err);
    })();
  }, [currentUserId]);

  const repost = useCallback((postId: string) => {
    const original = posts.find((p) => p.id === postId);
    if (!original || original.repostedBy.includes(currentUserId)) return;
    void track(AnalyticsEvent.CONTENT_SHARED, { content_type: 'post', item_id: postId, method: 'repost' });
    void (async () => {
      const { data, error: err } = await supabase.from('posts').insert({
        author_id: currentUserId,
        content: original.content,
        media_url: original.imageUrl ?? null,
        media_items: (original.mediaItems ?? null) as never,
        post_type: original.postType ?? null,
        category: original.category ?? null,
        original_post_id: asUuid(original.originalPostId || original.id),
        original_author_id: asUuid(original.originalAuthorId || original.authorId),
      }).select('*').maybeSingle();
      if (err || !data) {
        console.warn('[feed] repost failed', err);
        return;
      }
      setPosts((prev) => {
        const updated = prev.map((p) => p.id === postId ? { ...p, repostedBy: [...p.repostedBy, currentUserId] } : p);
        const entry: FeedPost = {
          id: data.id, authorId: currentUserId, authorName: authorName(currentUserId), authorInitials: authorInitials(currentUserId),
          category: original.category, postType: original.postType, content: original.content, imageUrl: original.imageUrl,
          mediaItems: original.mediaItems, createdAt: data.created_at,
          likes: [], comments: [], savedBy: [], repostedBy: [],
          originalPostId: original.originalPostId || original.id,
          originalAuthorId: original.originalAuthorId || original.authorId,
          originalAuthorName: original.originalAuthorName || original.authorName,
          archived: false,
        };
        return [entry, ...updated];
      });
    })();
  }, [posts, currentUserId]);

  const createPost = useCallback(async (data: { content: string; category?: string; imageUrl?: string; mediaItems?: MediaItem[]; postType?: PostType; taggedUserIds?: string[]; offerId?: string }) => {
    const user = getUserById(currentUserId);
    // Derive postType from author role so badges always reflect the real account type.
    const roleToType: Record<string, PostType> = {
      player: 'player', club: 'club', media: 'media', coach: 'coach', recruiter: 'recruiter',
    };
    const derivedType: PostType = data.postType ?? roleToType[user?.role ?? 'player'] ?? 'player';

    // Upload any inline media to storage first.
    const imageUrl = await uploadDataUrl('post-media', currentUserId, data.imageUrl);
    const mediaItems = data.mediaItems?.length
      ? await Promise.all(
          data.mediaItems.map(async (item) => ({
            ...item,
            url: (await uploadDataUrl('post-media', currentUserId, item.url)) ?? item.url,
            thumbnail: item.thumbnail ? await uploadDataUrl('post-media', currentUserId, item.thumbnail) : undefined,
          })),
        )
      : undefined;

    const { data: row, error: err } = await supabase.from('posts').insert({
      author_id: currentUserId,
      content: data.content,
      category: data.category ?? null,
      post_type: derivedType,
      media_url: imageUrl ?? null,
      media_items: mediaItems ?? null,
      tagged_user_ids: data.taggedUserIds?.length ? data.taggedUserIds : null,
      offer_id: asUuid(data.offerId),
    }).select('*').maybeSingle();

    if (err || !row) {
      console.warn('[feed] createPost failed', err);
      throw err ?? new Error('Failed to create post');
    }

    const newPost: FeedPost = {
      id: row.id, authorId: currentUserId, authorName: user?.name ?? 'Unknown', authorInitials: user?.initials ?? '??',
      category: data.category, postType: derivedType, content: data.content, imageUrl,
      mediaItems: mediaItems?.length ? mediaItems : undefined,
      createdAt: row.created_at, likes: [], comments: [], savedBy: [], repostedBy: [], archived: false,
      taggedUserIds: data.taggedUserIds?.length ? data.taggedUserIds : undefined,
      offerId: data.offerId,
    };
    setPosts((prev) => [newPost, ...prev]);
    void track(AnalyticsEvent.POST_CREATED, {
      post_id: row.id,
      post_type: derivedType,
      has_media: Boolean(imageUrl || mediaItems?.length),
      has_offer: Boolean(data.offerId),
    });
    return newPost.id;
  }, [currentUserId]);

  const sharePost = useCallback((postId: string) => {
    void track(AnalyticsEvent.CONTENT_SHARED, { content_type: 'post', item_id: postId, method: 'link' });
    const url = `${window.location.origin}/post/${postId}`;
    if (navigator.clipboard) navigator.clipboard.writeText(url);
  }, []);

  const editCaption = useCallback((postId: string, newContent: string, taggedUserIds?: string[]) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, content: newContent, taggedUserIds: taggedUserIds?.length ? taggedUserIds : undefined } : p)));
    void supabase
      .from('posts')
      .update({ content: newContent, tagged_user_ids: taggedUserIds?.length ? taggedUserIds : null })
      .eq('id', postId)
      .then(({ error: err }) => { if (err) console.warn('[feed] editCaption failed', err); });
  }, []);

  const getTaggedPosts = useCallback((userId: string) => {
    return posts.filter((p) => !p.archived && p.taggedUserIds?.includes(userId));
  }, [posts]);

  const deletePost = useCallback((postId: string, requesterId: string) => {
    const requester = getUserById(requesterId);
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    if (post.authorId !== requesterId && requester?.role !== 'admin') return;
    setPosts((prev) => prev.filter((p) => p.id !== postId && p.originalPostId !== postId));
    void supabase.from('posts').delete().eq('id', postId).then(({ error: err }) => {
      if (err) console.warn('[feed] deletePost failed', err);
    });
  }, [posts]);

  const setArchived = useCallback((postId: string, archived: boolean) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, archived } : p)));
    void supabase.from('posts').update({ archived }).eq('id', postId).then(({ error: err }) => {
      if (err) console.warn('[feed] archive toggle failed', err);
    });
  }, []);

  const archivePost = useCallback((postId: string) => setArchived(postId, true), [setArchived]);
  const restorePost = useCallback((postId: string) => setArchived(postId, false), [setArchived]);

  const reportPost = useCallback((postId: string, reason: string) => {
    setReports((prev) => [...prev, { id: genId(), postId, reporterId: currentUserId, reason, createdAt: new Date().toISOString() }]);
    // Persist so moderators actually receive the report (Apple Guideline 1.2).
    const post = posts.find((p) => p.id === postId);
    void submitReport(currentUserId, {
      subject: 'report_content',
      description: `Post reported — reason: ${reason}${post ? `\n\nAuthor: ${post.authorId}\nContent: ${post.content?.slice(0, 500) ?? ''}` : ''}`,
      targetType: 'post',
      targetId: postId,
    }).then(({ error: err }) => {
      if (err) console.warn('[feed] reportPost persist failed', err);
    });
  }, [currentUserId, posts]);


  const isLiked = useCallback((postId: string) => {
    const post = posts.find((p) => p.id === postId);
    return post?.likes.includes(currentUserId) ?? false;
  }, [posts, currentUserId]);

  const isSaved = useCallback((postId: string) => {
    const post = posts.find((p) => p.id === postId);
    return post?.savedBy.includes(currentUserId) ?? false;
  }, [posts, currentUserId]);

  const getSavedPosts = useCallback((userId: string) => posts.filter((p) => p.savedBy.includes(userId) && !p.archived), [posts]);
  const getArchivedPosts = useCallback((userId: string) => posts.filter((p) => p.authorId === userId && p.archived), [posts]);

  const deleteComment = useCallback((postId: string, commentId: string) => {
    setPosts((prev) => prev.map((p) => {
      if (p.id !== postId) return p;
      const updated = { ...p, comments: p.comments.filter((c) => c.id !== commentId) };
      // Clear pinned if pinned comment was deleted
      if (p.pinnedCommentId === commentId) updated.pinnedCommentId = undefined;
      return updated;
    }));
    void supabase.from('comments').delete().eq('id', commentId).then(({ error: err }) => {
      if (err) console.warn('[feed] deleteComment failed', err);
    });
  }, []);

  const pinComment = useCallback((postId: string, commentId: string | undefined) => {
    const previous = posts.find((p) => p.id === postId)?.pinnedCommentId;
    setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, pinnedCommentId: commentId } : p));
    void (async () => {
      if (previous) await supabase.from('comments').update({ pinned: false }).eq('id', previous);
      if (commentId) await supabase.from('comments').update({ pinned: true }).eq('id', commentId);
    })();
  }, [posts]);

  const dismissReport = useCallback((reportId: string) => {
    setReports((prev) => prev.filter((r) => r.id !== reportId));
  }, []);

  const getAllComments = useCallback(() => {
    return posts.flatMap((p) => p.comments.map((c) => ({ ...c, postId: p.id })));
  }, [posts]);

  // Comment report
  const reportComment = useCallback((postId: string, commentId: string, reason: CommentReportReason) => {
    setCommentReports((prev) => [...prev, {
      id: genId(), commentId, postId, reporterId: currentUserId, reason, createdAt: new Date().toISOString(),
    }]);
    const comment = posts.find((p) => p.id === postId)?.comments.find((c) => c.id === commentId);
    void submitReport(currentUserId, {
      subject: 'report_content',
      description: `Comment reported — reason: ${reason}${comment ? `\n\nAuthor: ${comment.authorId}\nComment: ${comment.text?.slice(0, 500) ?? ''}` : ''}\n\nPost: ${postId}`,
      targetType: 'comment',
      targetId: commentId,
    }).then(({ error: err }) => {
      if (err) console.warn('[feed] reportComment persist failed', err);
    });
  }, [currentUserId, posts]);


  const dismissCommentReport = useCallback((reportId: string) => {
    setCommentReports((prev) => prev.filter((r) => r.id !== reportId));
  }, []);

  const hideComment = useCallback((postId: string, commentId: string) => {
    setPosts((prev) => prev.map((p) => {
      if (p.id !== postId) return p;
      return { ...p, comments: p.comments.map((c) => c.id === commentId ? { ...c, hidden: true } : c) };
    }));
  }, []);

  const unhideComment = useCallback((postId: string, commentId: string) => {
    setPosts((prev) => prev.map((p) => {
      if (p.id !== postId) return p;
      return { ...p, comments: p.comments.map((c) => c.id === commentId ? { ...c, hidden: false } : c) };
    }));
  }, []);

  // Anti-spam check
  const canPostComment = useCallback((text: string): { allowed: boolean; message?: string } => {
    if (commentingDisabled.has(currentUserId)) {
      return { allowed: false, message: 'Commenting has been disabled for your account.' };
    }
    const now = Date.now();
    const recentSame = lastCommentTimes.find((e) => e.text === text.trim() && now - e.time < 10000);
    if (recentSame) {
      return { allowed: false, message: 'You just posted that. Try something different.' };
    }
    const recentCount = lastCommentTimes.filter((e) => now - e.time < 5000).length;
    if (recentCount >= 3) {
      return { allowed: false, message: 'Slow down! You\'re commenting too fast.' };
    }
    return { allowed: true };
  }, [currentUserId, lastCommentTimes, commentingDisabled]);

  const recordCommentTime = useCallback((text: string) => {
    const now = Date.now();
    setLastCommentTimes((prev) => [...prev.filter((e) => now - e.time < 15000), { text: text.trim(), time: now }]);
  }, []);

  // Admin: toggle commenting disabled for a user
  const toggleCommentingDisabled = useCallback((userId: string) => {
    setCommentingDisabled((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  }, []);

  const isCommentingDisabled = useCallback((userId: string) => commentingDisabled.has(userId), [commentingDisabled]);

  return {
    posts, loading, loadingMore, hasMore, loadMore, error, reload,
    createPost, toggleLike, addComment, toggleCommentLike, toggleSave, repost, sharePost,
    editCaption, deletePost, archivePost, restorePost, reportPost, deleteComment, pinComment,
    dismissReport, getAllComments, isLiked, isSaved, getSavedPosts, getArchivedPosts,
    getTaggedPosts, reports, sortByRelevance,
    // Comment moderation
    commentReports, reportComment, dismissCommentReport, hideComment, unhideComment,
    canPostComment, recordCommentTime, toggleCommentingDisabled, isCommentingDisabled,
  };
}
